import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  getAllUnsubscribedClients,
  getClientEmailHistories,
  getAdminEmailHistories,
} from "../services/Mail_service";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import { paginate } from "../utils/functions/pagination";
import { PaginationControls } from "./PaginationControls";
import dayjs, { Dayjs } from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import { DatePicker, Checkbox, Card, Space, Tooltip, Badge, Input, Tabs, Button, Typography, Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { 
  SearchOutlined, 
  CalendarOutlined, 
  MailOutlined, 
  UserOutlined, 
  FileTextOutlined,
  ExportOutlined,
  EyeOutlined,
  DownOutlined,
  UpOutlined,
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';

// Activer le plugin isBetween
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface UnsubscribedClient {
  id: number;
  firstname?: string;
  name?: string;
  email: string;
  createdAt?: Date | string;
  reason?: string;
  postal_code: string;
  country: string;
  adresse: string;
  user_type:string 
}

interface EmailDetails {
  name?: string;
  profession?: string;
  dirigeant?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  siret?: string;
  commentaires?: string;
  [key: string]: any;
}

interface EmailHistory {
  id: number;
  recipient: string;
  sender: string;
  subject: string;
  content: string;
  details: string | EmailDetails;
  createdAt: string;
  [key: string]: any;
}

function MailEnvoye() {
  const navigate = useNavigate();

  // États pour les onglets
  const [activeTab, setActiveTab] = useState<string>("desabonnes");

  // États pour les désabonnés (pagination client)
  const [desabonnesClients, setDesabonnesClients] = useState<UnsubscribedClient[]>([]);
  const [loadingDesabonnes, setLoadingDesabonnes] = useState(false);
  const [currentPageDesabonnes, setCurrentPageDesabonnes] = useState(1);
  const [desabonnesSearch, setDesabonnesSearch] = useState("");
  const [filteredDesabonnes, setFilteredDesabonnes] = useState<UnsubscribedClient[]>([]);

  // États pour les historiques clients (pagination serveur)
  const [clientEmailHistories, setClientEmailHistories] = useState<EmailHistory[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [currentPageClients, setCurrentPageClients] = useState(1);
  const [clientSearch, setClientSearch] = useState("");
  const [clientDateRange, setClientDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [totalPagesClients, setTotalPagesClients] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [loadingPageClients, setLoadingPageClients] = useState(false);

  // États pour les historiques admin (pagination serveur)
  const [adminEmailHistories, setAdminEmailHistories] = useState<EmailHistory[]>([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [currentPageAdmin, setCurrentPageAdmin] = useState(1);
  const [adminSearch, setAdminSearch] = useState("");
  const [adminDateRange, setAdminDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selectedAdminRecipients, setSelectedAdminRecipients] = useState<string[]>([]);
  const [totalPagesAdmin, setTotalPagesAdmin] = useState(1);
  const [totalAdmin, setTotalAdmin] = useState(0);
  const [loadingPageAdmin, setLoadingPageAdmin] = useState(false);

  // État pour les lignes expandées
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Prospects (users sans annonce)
  const [prospects, setProspects] = useState<UnsubscribedClient[]>([]);
  const [loadingProspect, setLoadingProspect] = useState(false);
  const [currentPageProspect, setCurrentPageProspect] = useState(1);
  const [prospectSearch, setProspectSearch] = useState("");
  const [filteredProspects, setFilteredProspects] = useState<UnsubscribedClient[]>([]);
  const [selectedProspects, setSelectedProspects] = useState<string[]>([]); // État pour les prospects sélectionnés

  const fetchProspects = async () => {
    setLoadingProspect(true);
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/userwithoutannonce`,
        {
          headers: { Authorization: token }
        }
      );

      const users = Array.isArray(response.data.users) ? response.data.users : [];
      setProspects(users);
      setFilteredProspects(users);
      setCurrentPageProspect(1);
      setSelectedProspects([]); // Réinitialiser la sélection
    } catch (error) {
      console.error("Erreur lors de la récupération des prospects :", error);
      setProspects([]);
      setFilteredProspects([]);
    } finally {
      setLoadingProspect(false);
    }
  };

  const itemsPerPage = 20;

  // Styles
  const styles = {
    container: {
      padding: "24px",
      maxWidth: "1400px",
      margin: "0 auto"
    } as const,
    header: {
      marginBottom: "24px"
    } as const,
    filterCard: {
      marginBottom: "24px",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
    } as const,
    filterRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
      alignItems: "end"
    } as const,
    tableContainer: {
      overflowX: "auto" as const,
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      background: "#fff",
      maxHeight: "600px",
      overflowY: "auto" as const
    } as const,
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      minWidth: "800px"
    } as const,
    th: {
      backgroundColor: "#4BB0C0",
      color: "white",
      padding: "16px 12px",
      fontSize: "14px",
      fontWeight: "600",
      textAlign: "left" as const,
      borderBottom: "2px solid #3a9aa8",
      position: "sticky" as const,
      top: 0,
      zIndex: 10
    } as const,
    td: {
      padding: "14px 12px",
      borderBottom: "1px solid #e8e8e8",
      fontSize: "14px"
    } as const,
    expandedContent: {
      background: "#f8f9fa",
      padding: "24px",
      borderRadius: "12px",
      margin: "16px",
      border: "1px solid #e5e7eb"
    } as const
  };

  // Fonctions utilitaires
  const parseDetails = (details: any): EmailDetails => {
    if (!details) return {};
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch (e) {
        console.error('Error parsing details:', e);
        return {};
      }
    }
    return details;
  };

  const toggleExpand = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Chargement initial des données
  useEffect(() => {
    if (activeTab === "desabonnes") {
      fetchDesabonnes();
    } else if (activeTab === "clients") {
      fetchClientEmailHistory(1, clientSearch);
    } else if (activeTab === "admin") {
      fetchAdminEmailHistory(1, adminSearch);
    } else if (activeTab === "prospect") {
      fetchProspects();
    }
  }, [activeTab]);

  // Fonctions de chargement des données
  const fetchDesabonnes = async () => {
    setLoadingDesabonnes(true);
    try {
      const unsubscribedClients = await getAllUnsubscribedClients();
      const clients = Array.isArray(unsubscribedClients) ? unsubscribedClients : [];
      setDesabonnesClients(clients);
      setFilteredDesabonnes(clients);
      setCurrentPageDesabonnes(1);
    } catch (error) {
      console.error("Erreur clients désabonnés", error);
      setDesabonnesClients([]);
      setFilteredDesabonnes([]);
    } finally {
      setLoadingDesabonnes(false);
    }
  };

  const fetchClientEmailHistory = async (page: number = 1, search: string = '') => {
    setLoadingClients(true);
    setLoadingPageClients(true);
    try {
      const response = await getClientEmailHistories(page, itemsPerPage, search);
      setClientEmailHistories(response.data);
      setTotalPagesClients(response.pagination.totalPages);
      setTotalClients(response.pagination.total);
      setCurrentPageClients(response.pagination.page);
    } catch (error) {
      console.error("Erreur historique emails clients", error);
      toast.error("Impossible de récupérer l'historique des emails des clients");
      setClientEmailHistories([]);
    } finally {
      setLoadingClients(false);
      setLoadingPageClients(false);
    }
  };

  const fetchAdminEmailHistory = async (page: number = 1, search: string = '') => {
    setLoadingAdmin(true);
    setLoadingPageAdmin(true);
    try {
      const response = await getAdminEmailHistories(page, itemsPerPage, search);
      setAdminEmailHistories(response.data);
      setTotalPagesAdmin(response.pagination.totalPages);
      setTotalAdmin(response.pagination.total);
      setCurrentPageAdmin(response.pagination.page);
    } catch (error) {
      console.error("Erreur historique emails admin", error);
      toast.error("Impossible de récupérer l'historique des emails de l'admin");
      setAdminEmailHistories([]);
    } finally {
      setLoadingAdmin(false);
      setLoadingPageAdmin(false);
    }
  };

  // Gestionnaires de recherche avec debounce
  useEffect(() => {
    if (activeTab === "clients") {
      const timer = setTimeout(() => {
        fetchClientEmailHistory(1, clientSearch);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [clientSearch, activeTab]);

  useEffect(() => {
    if (activeTab === "admin") {
      const timer = setTimeout(() => {
        fetchAdminEmailHistory(1, adminSearch);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [adminSearch, activeTab]);

  // Filtrage par date côté client (après chargement)
  useEffect(() => {
    if (clientDateRange && clientDateRange[0] && clientDateRange[1] && clientEmailHistories.length > 0) {
      const startDate = clientDateRange[0].startOf('day');
      const endDate = clientDateRange[1].endOf('day');
      const filtered = clientEmailHistories.filter((email) => {
        const emailDate = dayjs(email.createdAt);
        return emailDate.isBetween(startDate, endDate, null, '[]');
      });
      setClientEmailHistories(filtered);
      setTotalPagesClients(Math.ceil(filtered.length / itemsPerPage));
      setCurrentPageClients(1);
    } else if (!clientDateRange && clientEmailHistories.length > 0) {
      // Recharger si le filtre date est retiré
      fetchClientEmailHistory(1, clientSearch);
    }
  }, [clientDateRange]);

  useEffect(() => {
    if (adminDateRange && adminDateRange[0] && adminDateRange[1] && adminEmailHistories.length > 0) {
      const startDate = adminDateRange[0].startOf('day');
      const endDate = adminDateRange[1].endOf('day');
      const filtered = adminEmailHistories.filter((email) => {
        const emailDate = dayjs(email.createdAt);
        return emailDate.isBetween(startDate, endDate, null, '[]');
      });
      setAdminEmailHistories(filtered);
      setTotalPagesAdmin(Math.ceil(filtered.length / itemsPerPage));
      setCurrentPageAdmin(1);
    } else if (!adminDateRange && adminEmailHistories.length > 0) {
      fetchAdminEmailHistory(1, adminSearch);
    }
  }, [adminDateRange]);

  // Gestionnaires de pagination
  const handleClientPageChange = (newPage: number) => {
    if (clientDateRange) {
      // Si filtre date actif, pagination côté client
      setCurrentPageClients(newPage);
    } else {
      // Sinon, pagination côté serveur
      fetchClientEmailHistory(newPage, clientSearch);
    }
  };

  const handleAdminPageChange = (newPage: number) => {
    if (adminDateRange) {
      setCurrentPageAdmin(newPage);
    } else {
      fetchAdminEmailHistory(newPage, adminSearch);
    }
  };

  // Filtrage des désabonnés (pagination client)
  useEffect(() => {
    if (desabonnesClients.length > 0) {
      let filtered = desabonnesClients;
      if (desabonnesSearch) {
        const term = desabonnesSearch.toLowerCase();
        filtered = filtered.filter(client => 
          client.email?.toLowerCase().includes(term) ||
          client.reason?.toLowerCase().includes(term)
        );
      }
      setFilteredDesabonnes(filtered);
      setCurrentPageDesabonnes(1);
    }
  }, [desabonnesSearch, desabonnesClients]);

  // Filtrage des prospects
  useEffect(() => {
    if (prospects.length > 0) {
      let filtered = prospects;
      if (prospectSearch) {
        const term = prospectSearch.toLowerCase();
        filtered = filtered.filter(prospect => 
          prospect.email?.toLowerCase().includes(term) ||
          prospect.name?.toLowerCase().includes(term) ||
          prospect.firstname?.toLowerCase().includes(term)
        );
      }
      setFilteredProspects(filtered);
      setCurrentPageProspect(1);
      setSelectedProspects([]); // Réinitialiser la sélection lors du filtrage
    }
  }, [prospectSearch, prospects]);

  // Pagination côté client pour les désabonnés
  const paginatedDesabonnes = filteredDesabonnes.slice(
    (currentPageDesabonnes - 1) * itemsPerPage,
    currentPageDesabonnes * itemsPerPage
  );
  const totalPagesDesabonnes = Math.ceil(filteredDesabonnes.length / itemsPerPage);

  // Pagination pour les prospects
  const paginatedProspects = filteredProspects.slice(
    (currentPageProspect - 1) * itemsPerPage,
    currentPageProspect * itemsPerPage
  );
  const totalPagesProspects = Math.ceil(filteredProspects.length / itemsPerPage);

  // Fonctions de sélection pour les prospects
  const handleSelectProspect = (email: string, checked: boolean) => {
    setSelectedProspects(prev =>
      checked ? [...prev, email] : prev.filter(e => e !== email)
    );
  };

  const handleSelectAllProspects = (checked: boolean) => {
    if (checked) {
      const allProspectEmails = paginatedProspects.map(p => p.email).filter(email => email);
      setSelectedProspects(Array.from(new Set([...selectedProspects, ...allProspectEmails])));
    } else {
      const pageEmails = paginatedProspects.map(p => p.email);
      setSelectedProspects(selectedProspects.filter(email => !pageEmails.includes(email)));
    }
  };

  const handleImportProspects = () => {
    if (selectedProspects.length === 0) {
      toast.warning("Veuillez sélectionner au moins un prospect");
      return;
    }
    localStorage.setItem("importedRecipients", JSON.stringify(selectedProspects));
    toast.success(`${selectedProspects.length} prospect(s) sélectionné(s)`);
    navigate("/home/admin/emails?tab=EnvoyerMail");
  };

  // Fonctions de sélection pour l'admin
  const handleSelectAdminRecipient = (recipient: string, checked: boolean) => {
    setSelectedAdminRecipients(prev =>
      checked ? [...prev, recipient] : prev.filter(r => r !== recipient)
    );
  };

  const handleSelectAllAdmin = (checked: boolean) => {
    if (checked) {
      const allRecipients = adminEmailHistories.map(email => email.recipient);
      setSelectedAdminRecipients(Array.from(new Set([...selectedAdminRecipients, ...allRecipients])));
    } else {
      const pageRecipients = adminEmailHistories.map(email => email.recipient);
      setSelectedAdminRecipients(selectedAdminRecipients.filter(r => !pageRecipients.includes(r)));
    }
  };

  const handleImportAdminRecipients = () => {
    if (selectedAdminRecipients.length === 0) {
      toast.warning("Veuillez sélectionner au moins un destinataire");
      return;
    }
    localStorage.setItem("importedRecipients", JSON.stringify(selectedAdminRecipients));
    toast.success(`${selectedAdminRecipients.length} destinataire(s) sélectionné(s)`);
    navigate("/home/admin/emails?tab=EnvoyerMail");
  };

  // Fonction de réabonnement
  const handleAdminResubscribe = async (email: string, clientId?: number) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/email/admin/resubscribe`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(response.data.message);
      fetchDesabonnes();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erreur lors du réabonnement");
      console.error(error);
    }
  };

  const handleConfirmResubscribe = (email: string, clientId: number) => {
    Swal.fire({
      title: 'Confirmation',
      text: `Voulez-vous réabonner ${email} ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4BB0C0',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, réabonner',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        handleAdminResubscribe(email, clientId);
      }
    });
  };

  // Fonction d'export Excel
  const handleExportExcel = () => {
    if (!adminEmailHistories || adminEmailHistories.length === 0) return;

    const dataToExport = adminEmailHistories.map((emailItem) => {
      const details = parseDetails(emailItem.details);
      return {
        "Date d'envoi": new Date(emailItem.createdAt).toLocaleString(),
        "Destinataire Email": emailItem.recipient,
        "Expéditeur": emailItem.sender,
        "Objet": emailItem.subject,
        "Nom & Prénom": details.name || "-",
        "Profession": details.profession || "-",
        "Téléphone": details.telephone || "-",
        "Adresse": details.adresse || "-",
        "Code Postal": details.codePostal || "-",
        "Ville": details.ville || "-",
        "SIRET": details.siret || "-",
        "Dirigeant": details.dirigeant || "-",
        "Contenu": emailItem.content.replace(/<[^>]*>/g, "")
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emails Admin");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `emails_admin_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const isFirstAdminOccurrence = (recipient: string, index: number) => {
    return adminEmailHistories.findIndex(email => email.recipient === recipient) === index;
  };

  const allPageAdminSelected = adminEmailHistories.length > 0 &&
    adminEmailHistories.every(email => selectedAdminRecipients.includes(email.recipient));

  const allPageProspectsSelected = paginatedProspects.length > 0 &&
    paginatedProspects.every(prospect => prospect.email && selectedProspects.includes(prospect.email));

  // Composants mobiles
  const MobileUnsubscribedCard = ({ client }: { client: UnsubscribedClient }) => (
    <Card style={{ marginBottom: "12px", borderRadius: "8px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div><MailOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {client.email}</div>
        <div><CalendarOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> 
          {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : "N/A"}
        </div>
        {client.reason && (
          <div>
            <Badge color="red" text={client.reason} />
          </div>
        )}
        <Button 
          type="primary" 
          size="small"
          onClick={() => handleConfirmResubscribe(client.email, client.id)}
          style={{ backgroundColor: "#4BB0C0", borderColor: "#4BB0C0", marginTop: "8px" }}
        >
          Réabonner
        </Button>
      </div>
    </Card>
  );

  const MobileEmailCard = ({ email, type }: { email: EmailHistory; type: 'client' | 'admin' }) => {
    const details = parseDetails(email.details);
    const isExpanded = expandedRow === email.id;
    const isSelected = type === 'admin' && selectedAdminRecipients.includes(email.recipient);

    return (
      <Card 
        style={{ 
          marginBottom: "12px",
          borderRadius: "8px",
          border: isSelected ? "2px solid #4BB0C0" : "1px solid #f0f0f0"
        }}
      >
        {type === 'admin' && (
          <div style={{ marginBottom: "8px" }}>
            <Checkbox
              checked={isSelected}
              onChange={(e) => handleSelectAdminRecipient(email.recipient, e.target.checked)}
            />
          </div>
        )}
        
        <div style={{ display: "grid", gap: "8px" }}>
          <div><CalendarOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> 
            {new Date(email.createdAt).toLocaleDateString()}
          </div>
          <div><MailOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {email.recipient}</div>
          {details.name && <div><UserOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {details.name}</div>}
          {details.profession && <div><FileTextOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {details.profession}</div>}
        </div>

        <div style={{ marginTop: "12px", textAlign: "right" }}>
          <Button 
            type="link" 
            onClick={() => toggleExpand(email.id)}
            style={{ color: "#4BB0C0" }}
          >
            {isExpanded ? "Réduire" : "Voir détails"} {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </Button>
        </div>

        {isExpanded && (
          <div style={{ marginTop: "16px", padding: "12px", background: "#f9f9f9", borderRadius: "8px" }}>
            <Text strong>Objet: </Text> {email.subject}
            <div style={{ marginTop: "8px" }}>
              <Text strong>Message:</Text>
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  background: "#fff",
                  borderRadius: "4px",
                  maxHeight: "150px",
                  overflow: "auto"
                }}
                dangerouslySetInnerHTML={{ __html: email.content || "" }}
              />
            </div>
          </div>
        )}
      </Card>
    );
  };

const MobileProspectCard = ({ prospect }: { prospect: UnsubscribedClient }) => {
  const isSelected = prospect.email ? selectedProspects.includes(prospect.email) : false;

  return (
    <Card 
      style={{ 
        marginBottom: "12px",
        borderRadius: "8px",
        border: isSelected ? "2px solid #4BB0C0" : "1px solid #f0f0f0"
      }}
    >
      <div style={{ marginBottom: "8px" }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            if (prospect.email) {
              handleSelectProspect(prospect.email, e.target.checked);
            }
          }}
          disabled={!prospect.email}
        />
      </div>
      
      <div style={{ display: "grid", gap: "8px" }}>
        <div><UserOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> 
          {prospect.name || "-"} {prospect.firstname || "-"}
        </div>
        <div><MailOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {prospect.email || "-"}</div>
        <div><FileTextOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> 
          {prospect.user_type || "-"}
        </div>
        <div><CalendarOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> 
          {prospect.adresse || "-"} {prospect.postal_code || "-"} {prospect.country || "-"}
        </div>
      </div>
    </Card>
  );
};

  if (loadingDesabonnes && activeTab === "desabonnes") {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <FaSpinner className="animate-spin" size={30} color="#4BB0C0" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Title level={2} style={{ color: "#333", marginBottom: "8px" }}>
          Gestion des Emails
        </Title>
        <Text type="secondary">
          Consultez l'historique des emails et gérez les désabonnements
        </Text>
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" style={{ marginBottom: "24px" }}>
        <TabPane tab="Clients désabonnés" key="desabonnes" />
        <TabPane tab="Emails des clients" key="clients" />
        <TabPane tab="Emails de l'admin" key="admin" />
        <TabPane tab="Prospects" key="prospect" />  
      </Tabs>

      {/* Contenu par onglet */}
      <div className="mt-4">
        {/* Clients désabonnés */}
        {activeTab === "desabonnes" && (
          <>
            <Card style={styles.filterCard}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={18}>
                  <Input
                    placeholder="Rechercher par email ou motif..."
                    value={desabonnesSearch}
                    onChange={e => setDesabonnesSearch(e.target.value)}
                    prefix={<SearchOutlined style={{ color: "#4BB0C0" }} />}
                    allowClear
                    size="large"
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={fetchDesabonnes}
                    style={{ width: "100%", height: "40px" }}
                  >
                    Actualiser
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Version Desktop - Tableau */}
            <div className="d-none d-md-block" style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Date de désabonnement</th>
                    <th style={styles.th}>Motif</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDesabonnes.length > 0 ? (
                    paginatedDesabonnes.map((client) => (
                      <tr key={client.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={styles.td}>
                          <Space>
                            <MailOutlined style={{ color: "#4BB0C0" }} />
                            {client.email}
                          </Space>
                        </td>
                        <td style={styles.td}>
                          <Space>
                            <CalendarOutlined style={{ color: "#4BB0C0" }} />
                            {client.createdAt
                              ? new Date(client.createdAt).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })
                              : "N/A"}
                          </Space>
                        </td>
                        <td style={styles.td}>
                          <Badge color="red" text={client.reason || "Non spécifié"} />
                        </td>
                        <td style={styles.td}>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleConfirmResubscribe(client.email, client.id)}
                            style={{ backgroundColor: "#4BB0C0", borderColor: "#4BB0C0" }}
                          >
                            Réabonner
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ padding: "64px 24px", textAlign: "center", color: "#6b7280" }}>
                        Aucun désabonnement trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Version Mobile - Cards */}
            <div className="d-block d-md-none">
              {paginatedDesabonnes.map((client) => (
                <MobileUnsubscribedCard key={client.id} client={client} />
              ))}
            </div>

            {/* Pagination Désabonnés */}
            {paginatedDesabonnes.length > 0 && totalPagesDesabonnes > 1 && (
              <div style={{ marginTop: "24px", display: "flex", justifyContent: "center" }}>
                <PaginationControls
                  currentPage={currentPageDesabonnes}
                  totalPages={totalPagesDesabonnes}
                  onPageChange={setCurrentPageDesabonnes}
                />
              </div>
            )}
          </>
        )}

        {/* Emails clients */}
        {activeTab === "clients" && (
          <>
            <Card style={styles.filterCard}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={10}>
                  <Input
                    placeholder="Rechercher par email, nom, profession..."
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    prefix={<SearchOutlined style={{ color: "#4BB0C0" }} />}
                    allowClear
                    size="large"
                  />
                </Col>
                <Col xs={24} md={8}>
                  <RangePicker
                    value={clientDateRange}
                    onChange={(dates) => setClientDateRange(dates as any)}
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => {
                      setClientSearch("");
                      setClientDateRange(null);
                      fetchClientEmailHistory(1, "");
                    }} 
                    style={{ width: "100%", height: "40px" }}
                  >
                    Actualiser
                  </Button>
                </Col>
              </Row>
            </Card>

            {loadingClients ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FaSpinner className="animate-spin" size={30} color="#4BB0C0" />
              </div>
            ) : clientEmailHistories.length > 0 ? (
              <>
                {/* Version Desktop - Tableau */}
                <div className="d-none d-md-block" style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date d'envoi</th>
                        <th style={styles.th}>Expéditeur</th>
                        <th style={styles.th}>Destinataire</th>
                        <th style={styles.th}>Nom & Prénom</th>
                        <th style={styles.th}>Profession</th>
                        <th style={styles.th}>Dirigeant</th>
                        <th style={styles.th}>Contenu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientEmailHistories.map((emailItem) => {
                        const details = parseDetails(emailItem.details);
                        const isExpanded = expandedRow === emailItem.id;

                        return (
                          <React.Fragment key={emailItem.id}>
                            <tr onClick={() => toggleExpand(emailItem.id)} style={{ cursor: "pointer" }}>
                              <td style={styles.td}>
                                <Space>
                                  <CalendarOutlined style={{ color: "#4BB0C0" }} />
                                  {new Date(emailItem.createdAt).toLocaleDateString()}
                                </Space>
                              </td>
                              <td style={styles.td}>
                                <Tooltip title="Expéditeur">
                                  <Space>
                                    <MailOutlined style={{ color: "#4BB0C0" }} />
                                    {emailItem.sender}
                                  </Space>
                                </Tooltip>
                              </td>
                              <td style={styles.td}>{emailItem.recipient}</td>
                              <td style={styles.td}>{details.name || "-"}</td>
                              <td style={styles.td}>{details.profession || "-"}</td>
                              <td style={styles.td}>{details.dirigeant || "-"}</td>
                              <td style={styles.td}>
                                <Space>
                                  <EyeOutlined style={{ color: "#4BB0C0" }} />
                                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                </Space>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr>
                                <td colSpan={7} style={{ padding: 0, background: "#f9f9f9" }}>
                                  <div style={styles.expandedContent}>
                                    <h4 style={{ margin: "0 0 16px 0", color: "#333" }}>
                                      Détails de l'email
                                    </h4>
                                    <div style={{ display: "grid", gap: "16px" }}>
                                      <div>
                                        <Text type="secondary">Objet</Text>
                                        <div style={{ fontWeight: 500 }}>{emailItem.subject}</div>
                                      </div>
                                      <div>
                                        <Text type="secondary">Message</Text>
                                        <div
                                          style={{
                                            padding: "16px",
                                            background: "#fff",
                                            borderRadius: "8px",
                                            maxHeight: "300px",
                                            overflow: "auto"
                                          }}
                                          dangerouslySetInnerHTML={{ __html: emailItem.content || "" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Version Mobile - Cards */}
                <div className="d-block d-md-none">
                  {clientEmailHistories.map((email) => (
                    <MobileEmailCard key={email.id} email={email} type="client" />
                  ))}
                </div>

                {/* Pagination Clients */}
                {totalPagesClients > 1 && (
                  <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <PaginationControls
                      currentPage={currentPageClients}
                      totalPages={totalPagesClients}
                      onPageChange={handleClientPageChange}
                    />
                    {loadingPageClients && <FaSpinner className="animate-spin" style={{ marginLeft: '10px' }} />}
                  </div>
                )}
              </>
            ) : (
              <Card style={{ textAlign: "center", padding: "40px" }}>
                <MailOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
                <Title level={4}>Aucun email trouvé</Title>
                <Text type="secondary">Aucun email n'a été envoyé à des clients.</Text>
              </Card>
            )}
          </>
        )}

        {/* Emails admin */}
        {activeTab === "admin" && (
          <>
            <Card style={styles.filterCard}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Input
                    placeholder="Rechercher par email, nom, profession..."
                    value={adminSearch}
                    onChange={e => setAdminSearch(e.target.value)}
                    prefix={<SearchOutlined style={{ color: "#4BB0C0" }} />}
                    allowClear
                    size="large"
                  />
                </Col>
                <Col xs={24} md={6}>
                  <RangePicker
                    value={adminDateRange}
                    onChange={(dates) => setAdminDateRange(dates as any)}
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Col>
                <Col xs={24} md={5}>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => {
                      setAdminSearch("");
                      setAdminDateRange(null);
                      fetchAdminEmailHistory(1, "");
                    }}
                    style={{ width: "100%", height: "40px" }}
                  >
                    Actualiser
                  </Button>
                </Col>
                <Col xs={24} md={5}>
                  {adminEmailHistories.length > 0 && (
                    <Button 
                      type="primary"
                      icon={<ExportOutlined />} 
                      onClick={handleExportExcel}
                      style={{ width: "100%", height: "40px", backgroundColor: "#4BB0C0", borderColor: "#4BB0C0" }}
                    >
                      Exporter Excel
                    </Button>
                  )}
                </Col>
              </Row>

              {selectedAdminRecipients.length > 0 && (
                <Row style={{ marginTop: "16px" }}>
                  <Col span={24}>
                    <Button
                      type="primary"
                      icon={<MailOutlined />}
                      onClick={handleImportAdminRecipients}
                      style={{ backgroundColor: "#ef914f", borderColor: "#ef914f" }}
                    >
                      <span style ={{color:"white"}}>Envoyer un email à {selectedAdminRecipients.length} destinataire(s)</span>
                    </Button>
                  </Col>
                </Row>
              )}
            </Card>

            {loadingAdmin ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FaSpinner className="animate-spin" size={30} color="#4BB0C0" />
              </div>
            ) : adminEmailHistories.length > 0 ? (
              <>
                {/* Version Desktop - Tableau */}
                <div className="d-none d-md-block" style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, width: "50px" }}>
                          <Checkbox
                            checked={allPageAdminSelected}
                            onChange={(e) => handleSelectAllAdmin(e.target.checked)}
                          />
                        </th>
                        <th style={styles.th}>Date d'envoi</th>
                        <th style={styles.th}>Destinataire</th>
                        <th style={styles.th}>Nom & Prénom</th>
                        <th style={styles.th}>Profession</th>
                        <th style={styles.th}>Dirigeant</th>
                        <th style={styles.th}>Contenu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminEmailHistories.map((emailItem, index) => {
                        const details = parseDetails(emailItem.details);
                        const isExpanded = expandedRow === emailItem.id;

                        return (
                          <React.Fragment key={emailItem.id}>
                            <tr onClick={() => toggleExpand(emailItem.id)} style={{ cursor: "pointer" }}>
                              <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                {isFirstAdminOccurrence(emailItem.recipient, index) ? (
                                  <Checkbox
                                    checked={selectedAdminRecipients.includes(emailItem.recipient)}
                                    onChange={(e) => handleSelectAdminRecipient(emailItem.recipient, e.target.checked)}
                                  />
                                ) : (
                                  <Checkbox
                                    checked={selectedAdminRecipients.includes(emailItem.recipient)}
                                    disabled
                                  />
                                )}
                              </td>
                              <td style={styles.td}>
                                <Space>
                                  <CalendarOutlined style={{ color: "#4BB0C0" }} />
                                  {new Date(emailItem.createdAt).toLocaleDateString()}
                                </Space>
                              </td>
                              <td style={styles.td}>
                                <Tooltip title="Email du destinataire">
                                  <Space>
                                    <MailOutlined style={{ color: "#4BB0C0" }} />
                                    {emailItem.recipient}
                                  </Space>
                                </Tooltip>
                              </td>
                              <td style={styles.td}>{details.name || "-"}</td>
                              <td style={styles.td}>{details.profession || "-"}</td>
                              <td style={styles.td}>{details.dirigeant || "-"}</td>
                              <td style={styles.td}>
                                <Space>
                                  <EyeOutlined style={{ color: "#4BB0C0" }} />
                                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                </Space>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr>
                                <td colSpan={7} style={{ padding: 0, background: "#f9f9f9" }}>
                                  <div style={styles.expandedContent}>
                                    <h4 style={{ margin: "0 0 16px 0", color: "#333" }}>
                                      Détails de l'email
                                    </h4>
                                    <div style={{ display: "grid", gap: "16px" }}>
                                      <div>
                                        <Text type="secondary">Objet</Text>
                                        <div style={{ fontWeight: 500 }}>{emailItem.subject}</div>
                                      </div>
                                      <div>
                                        <Text type="secondary">Message</Text>
                                        <div
                                          style={{
                                            padding: "16px",
                                            background: "#fff",
                                            borderRadius: "8px",
                                            maxHeight: "300px",
                                            overflow: "auto"
                                          }}
                                          dangerouslySetInnerHTML={{ __html: emailItem.content || "" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Version Mobile - Cards */}
                <div className="d-block d-md-none">
                  {adminEmailHistories.map((email) => (
                    <MobileEmailCard key={email.id} email={email} type="admin" />
                  ))}
                </div>

                {/* Pagination Admin */}
                {totalPagesAdmin > 1 && (
                  <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <PaginationControls
                      currentPage={currentPageAdmin}
                      totalPages={totalPagesAdmin}
                      onPageChange={handleAdminPageChange}
                    />
                    {loadingPageAdmin && <FaSpinner className="animate-spin" style={{ marginLeft: '10px' }} />}
                  </div>
                )}
              </>
            ) : (
              <Card style={{ textAlign: "center", padding: "40px" }}>
                <MailOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
                <Title level={4}>Aucun email trouvé</Title>
                <Text type="secondary">Aucun email n'a été envoyé par l'admin.</Text>
              </Card>
            )}
          </>
        )}

        {/* Onglet Prospects */}
        {activeTab === "prospect" && (
          <>
            <Card style={styles.filterCard}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={14}>
                  <Input
                    placeholder="Rechercher par email, nom ou prénom..."
                    value={prospectSearch}
                    onChange={e => setProspectSearch(e.target.value)}
                    prefix={<SearchOutlined style={{ color: "#4BB0C0" }} />}
                    allowClear
                    size="large"
                  />
                </Col>
                <Col xs={24} md={5}>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={fetchProspects}
                    style={{ width: "100%", height: "40px" }}
                  >
                    Actualiser
                  </Button>
                </Col>
                <Col xs={24} md={5}>
                  {filteredProspects.length > 0 && (
                    <Button 
                      type="primary"
                      icon={<TeamOutlined />} 
                      onClick={handleImportProspects}
                      style={{ width: "100%", height: "40px", backgroundColor: "#ef914f", borderColor: "#ef914f" }}
                    >
                      <span style ={{color:"white"}}>{selectedProspects.length > 0 
                        ? `Envoyer un email à ${selectedProspects.length} prospect(s)`
                        : "Sélectionner des prospects"
                      }</span>
                    </Button>
                  )}
                </Col>
              </Row>
            </Card>

            {loadingProspect ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FaSpinner className="animate-spin" size={30} color="#4BB0C0" />
              </div>
            ) : filteredProspects.length > 0 ? (
              <>
                {/* Version Desktop - Tableau */}
                <div className="d-none d-md-block" style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, width: "50px" }}>
                          <Checkbox
                            checked={allPageProspectsSelected}
                            onChange={(e) => handleSelectAllProspects(e.target.checked)}
                          />
                        </th>
                        <th style={styles.th}>Nom & Prénom(s)</th>
                        <th style={styles.th}>Adresse</th>
                        <th style={styles.th}>Email</th>
                        <th style={styles.th}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProspects.map(user => (
                        <tr key={user.id}>
                          <td style={styles.td}>
                            {user.email ? (
                              <Checkbox
                                checked={selectedProspects.includes(user.email)}
                                onChange={(e) => handleSelectProspect(user.email, e.target.checked)}
                              />
                            ) : (
                              <Checkbox disabled />
                            )}
                          </td>
                          <td style={styles.td}>{user.name || "-"} {user.firstname || "-"}</td>
                          <td style={styles.td}>{user.adresse || "-"} {user.postal_code || "-"} {user.country || "-"}</td>
                          <td style={styles.td}>{user.email || "-"}</td>
                          <td style={styles.td}>{user.user_type || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Version Mobile - Cards */}
                <div className="d-block d-md-none">
                  {paginatedProspects.map(user => (
                    <MobileProspectCard key={user.id} prospect={user} />
                  ))}
                </div>

                {/* Pagination Prospects */}
                {filteredProspects.length > itemsPerPage && (
                  <div style={{ marginTop: "24px", display: "flex", justifyContent: "center" }}>
                    <PaginationControls
                      currentPage={currentPageProspect}
                      totalPages={totalPagesProspects}
                      onPageChange={setCurrentPageProspect}
                    />
                  </div>
                )}
              </>
            ) : (
              <Card style={{ textAlign: "center", padding: "40px" }}>
                <UserOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
                <Title level={4}>Aucun prospect trouvé</Title>
                <Text type="secondary">Aucun utilisateur sans annonce n'a été trouvé.</Text>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MailEnvoye;